import React, { useEffect, useState } from 'react';
import { FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { ESC_KEY_CODE } from '@components/Modal/Login/LoginModal';
import { ModalTypes } from '@components/Modal/Modal';
import TrainingChaptersTemplate from '@components/TrainingChapters/TrainingChapters';
import TrainingMaterialTemplate from '@components/TrainingMaterial/TrainingMaterial';
import useTranslations from '@hooks/useTranslation';
import ButtonTemplate from '@ui/Button/Button';
import Cookies from 'js-cookie';
import moment from 'moment/moment';
import { ACCESS_TOKEN_KEY, ACCESS_TOKEN_KEY_GUEST, REFRESH_TOKEN_KEY, ROUTES } from '../../constants';
import { ITrainingItemDate } from '../../interfaces/Calendar';
import { authApi } from '../../services/auth.service';
import { calendarApi } from '../../services/calendar.service';
import { RootState } from '../../store';
import { openModalType, setIsLoggedIn as setIsLoggedInState } from '../../store/ui';
import {
    Message,
    NoLoggedIn,
    OpenViewerButton,
    Player,
    PlayerCloseButton,
    PlayerWrapper,
    Record,
    RecordPlayer,
    Tab,
    Tabs,
    TabsBorderBottom,
    Wrapper,
    Zoom,
} from './TrainingStream.elements';

export interface TrainingStreamProps {
    dates: ITrainingItemDate[];
}

enum STREAM_STATUS {
    IS_ACTIVE = 'active',
    IS_INACTIVE = 'inactive',
}

const TrainingStreamTemplate: FC<TrainingStreamProps> = ({ dates: trainingDates }) => {
    const { t } = useTranslations();

    const dispatch = useDispatch();

    const router = useRouter();

    const isLoggedInState = useSelector((state: RootState) => state.ui.isLoggedIn);

    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [dates, setDates] = useState([]);
    const [activeDate, setActiveDate] = useState(null);
    const [isWaitingOnRecord, setIsWaitingOnRecord] = useState(false);
    const [isViewerActive, setIsViewerActive] = useState(false);
    const [orderedVideos, setOrderedVideos] = useState([]);

    useEffect(() => {
        if (trainingDates?.length > 0) {
            const filteredDates = trainingDates.filter(
                (date) => date.streams?.length > 0 || date.attachments?.length > 0 || date.videos?.length > 0
            );

            setDates(filteredDates);
            setActiveDate(filteredDates?.find((date) => date.date._key === router.query.termin) || filteredDates?.[0]);
        }
    }, [trainingDates]);

    useEffect(() => {
        setIsLoggedIn(isLoggedInState);
    }, [isLoggedInState]);

    useEffect(() => {
        if (
            moment().local().diff(activeDate?.date?.to, 'minutes') > 0 &&
            activeDate?.streams?.length === 0 &&
            activeDate?.videos?.length === 0
        ) {
            return setIsWaitingOnRecord(true);
        }

        if (
            moment().local().diff(activeDate?.date?.to, 'minutes') > 0 &&
            activeDate?.streams?.length > 0 &&
            activeDate?.videos?.length === 0
        ) {
            calendarApi.getStreamStatus(activeDate?.streams[0]?._key).then(({ data }) => {
                if (data?.status === STREAM_STATUS.IS_INACTIVE) {
                    setIsWaitingOnRecord(true);
                }
            });
        } else {
            setIsWaitingOnRecord(false);
        }

        setOrderedVideos(activeDate?.videos.sort((a: any, b: any) => a.order - b.order));
    }, [activeDate]);

    useEffect(() => {
        orderedVideos?.map((video: { url: string; _key: string; timeWatched: number }) => {
            const element = document.getElementById(`video-${video._key}`);

            if (element) {
                element.setAttribute('controlslist', 'nodownload');
            }

            if (video && element) {
                (element as HTMLVideoElement).currentTime = video?.timeWatched;
                element.addEventListener('pause', async () => {
                    await calendarApi.updateWatchedTimeToVideo(video._key, (element as HTMLVideoElement).currentTime);
                });
            }
        });
    }, [orderedVideos]);

    useEffect(() => {
        setInterval(() => {
            const countOfMinutesToStart = moment().local().diff(activeDate?.date?.from, 'minutes');

            if (countOfMinutesToStart >= -5 && !activeDate?.streams?.[0]?.url && activeDate?.videos?.length === 0) {
                calendarApi
                    .getTrainingDates(router.query.slug as string, Cookies.get(ACCESS_TOKEN_KEY))
                    .then(({ data }) => {
                        const filteredDates = data?.filter(
                            (date: ITrainingItemDate) => date.streams?.length > 0 || date.attachments?.length > 0
                        );

                        setDates(filteredDates);
                    });
            }
        }, 60 * 1000);
    }, [activeDate]);

    useEffect(() => {
        const element = document.getElementById('stream');
        const isRunning =
            moment().local().diff(activeDate?.date?.from, 'minutes') >= 0 &&
            moment().local().diff(activeDate?.date?.to, 'minutes') <= 0;

        if (activeDate?.accessible && isRunning && activeDate?.streams?.[0] && element) {
            setInterval(async () => {
                try {
                    const { data } = await authApi.refresh(Cookies.get(REFRESH_TOKEN_KEY));
                    Cookies.set(ACCESS_TOKEN_KEY, data.access_token);
                    Cookies.set(REFRESH_TOKEN_KEY, data.refresh_token, { expires: 3 });
                } catch (error: any) {
                    if (error?.response?.status === 401) {
                        Cookies.remove(ACCESS_TOKEN_KEY);
                        Cookies.remove(REFRESH_TOKEN_KEY);
                        dispatch(setIsLoggedInState(false));

                        router.push({
                            pathname: ROUTES.HOME,
                        });
                    }
                }
            }, 10 * 60 * 1000);
        }
    }, [activeDate]);

    useEffect(() => {
        document.addEventListener('keyup', async (event) => {
            event.preventDefault();
            if (event.keyCode === ESC_KEY_CODE) {
                await setIsViewerActive(false);
            }
        });
    }, []);

    return (
        dates?.length > 0 && (
            <Wrapper>
                <Tabs>
                    {dates?.map((date, index) => {
                        const formattedDateFrom = moment(date.date.from).local().format('DD. MM. YYYY');
                        const formattedDateTo = moment(date.date.to).local().format('DD. MM. YYYY');

                        return (
                            <Tab
                                onClick={() => setActiveDate(date)}
                                key={index}
                                className={activeDate?.date?._key === date.date._key ? 'selected' : ''}
                            >
                                {isLoggedIn && date?.streams?.length === 0 ? '' : t('training.detail.stream.title')}{' '}
                                <br /> ({formattedDateFrom}
                                {formattedDateTo && formattedDateFrom !== formattedDateTo
                                    ? ` - ${formattedDateTo}`
                                    : ''}
                                )
                            </Tab>
                        );
                    })}
                    <TabsBorderBottom />
                </Tabs>
                {moment().local().diff(activeDate?.date?.from, 'minutes') <= -60 && activeDate?.isOnline && (
                    <NoLoggedIn>
                        <Message>{t('training.detail.stream.waiting.message1')}</Message>
                        <Message>{t('training.detail.stream.waiting.message2')}</Message>
                    </NoLoggedIn>
                )}
                {!isLoggedIn &&
                    !Cookies.get(ACCESS_TOKEN_KEY_GUEST) &&
                    activeDate?.streams?.length > 0 &&
                    moment().local().diff(activeDate?.date?.from, 'minutes') > -60 &&
                    !isWaitingOnRecord &&
                    moment().local().diff(activeDate?.date?.to, 'minutes') < 120 && (
                        <NoLoggedIn>
                            <Message>{t('training.detail.stream.noLoggedIn.message1')}</Message>
                            <Message>{t('training.detail.stream.noLoggedIn.message2')}</Message>
                            <ButtonTemplate
                                marginTop={20}
                                onClick={() =>
                                    !isLoggedIn &&
                                    dispatch(
                                        openModalType({
                                            type: ModalTypes.LOGIN,
                                        })
                                    )
                                }
                                text={t('training.detail.button.loggedIn')}
                            />
                        </NoLoggedIn>
                    )}
                {(isLoggedIn || Cookies.get(ACCESS_TOKEN_KEY_GUEST)) &&
                    activeDate &&
                    !activeDate?.accessible &&
                    moment().local().diff(activeDate?.date?.from, 'minutes') > -60 &&
                    !isWaitingOnRecord &&
                    moment().local().diff(activeDate?.date?.to, 'minutes') < 120 && (
                        <NoLoggedIn>
                            <Message>{t('training.detail.stream.noAccessible.message1')}</Message>
                        </NoLoggedIn>
                    )}
                {!isLoggedIn && !Cookies.get(ACCESS_TOKEN_KEY_GUEST) && activeDate?.videos?.length > 0 && (
                    <NoLoggedIn>
                        <Message>{t('training.detail.record.noLoggedIn.message1')}</Message>
                        <Message>{t('training.detail.record.noLoggedIn.message2')}</Message>
                    </NoLoggedIn>
                )}
                {!isLoggedIn &&
                    !Cookies.get(ACCESS_TOKEN_KEY_GUEST) &&
                    activeDate?.videos?.length === 0 &&
                    (isWaitingOnRecord || moment().local().diff(activeDate?.date?.to, 'minutes') > 120) && (
                        <NoLoggedIn>
                            <Message>{t('training.detail.stream.cancelled.message1')}</Message>
                        </NoLoggedIn>
                    )}
                {(isLoggedIn || Cookies.get(ACCESS_TOKEN_KEY_GUEST)) &&
                    activeDate?.streams?.length > 0 &&
                    (isWaitingOnRecord || moment().local().diff(activeDate?.date?.to, 'minutes') > 120) && (
                        <NoLoggedIn>
                            <Message>{t('training.detail.stream.cancelled.message1')}</Message>
                        </NoLoggedIn>
                    )}
                {activeDate?.accessible &&
                    activeDate?.streams?.[0]?.room_id &&
                    activeDate?.streams?.[0]?.url &&
                    moment().local().diff(activeDate?.date?.from, 'minutes') > -60 &&
                    !isWaitingOnRecord && (
                        <>
                            <PlayerWrapper className={isViewerActive ? 'viewer' : ''}>
                                <Player
                                    name="studiow"
                                    target="studiow"
                                    sandbox="allow-forms allow-top-navigation allow-scripts allow-same-origin"
                                    src={activeDate?.streams?.[0]?.url}
                                    className={isViewerActive ? 'viewer' : ''}
                                />
                            </PlayerWrapper>
                            {isViewerActive && (
                                <PlayerCloseButton onClick={() => setIsViewerActive(false)}>&#10005;</PlayerCloseButton>
                            )}
                            <OpenViewerButton onClick={() => setIsViewerActive(true)}>
                                {t('training.detail.stream.viewer.button')}
                            </OpenViewerButton>
                        </>
                    )}
                {activeDate?.accessible &&
                    orderedVideos?.[0]?.url &&
                    orderedVideos?.map((video: { url: string; _key: string; chapters: [] }, key: number) => (
                        <>
                            <Record key={key}>
                                <RecordPlayer id={`video-${video._key}`} allow="fullscreen" controls>
                                    <source src={video?.url} type="video/mp4" />
                                </RecordPlayer>
                            </Record>
                            <TrainingChaptersTemplate chapters={video?.chapters} videoKey={`video-${video._key}`} />
                        </>
                    ))}
                {activeDate?.accessible &&
                    !activeDate?.streams?.[0]?.room_id &&
                    activeDate?.streams?.[0]?.url &&
                    moment().local().diff(activeDate?.date?.from, 'minutes') > -60 &&
                    moment().local().diff(activeDate.date.to, 'minutes') <= 120 && (
                        <Zoom>
                            <ButtonTemplate
                                onClick={() => window.open(activeDate?.streams?.[0]?.url, '_blank')}
                                text={t('training.detail.button.zoom')}
                            />
                        </Zoom>
                    )}
                {activeDate?.accessible &&
                    (activeDate?.attachments?.length > 0 || activeDate?.videos?.[0]?.chapters?.length > 0) && (
                        <TrainingMaterialTemplate attachments={activeDate?.attachments} />
                    )}
            </Wrapper>
        )
    );
};

export default TrainingStreamTemplate;
